import { UiService } from 'src/app/services/ui.service';
import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AccountService } from 'src/app/services/user/account.service';
import { CountryCodeModalComponent } from '../modals/country-code-modal/country-code-modal.component';
import { ModalController } from '@ionic/angular';
import { CityListComponent } from '../city-list/city-list.component';
import { SelectCountryModalComponent } from '../modals/select-country-modal/select-country-modal.component';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { ThemeService } from 'src/app/services/client/theme/theme.service';
@Component({
  selector: 'app-guest-form',
  templateUrl: './guest-form.component.html',
  styleUrls: ['./guest-form.component.scss'],
})
export class GuestFormComponent implements OnInit {
  @Input() guest: any = {};
  public selectedCountryCode: any;
  public guestForm: UntypedFormGroup;
  public currentHeaderData: any;
  public guestId: any;
  public guestSource: any;
  public isCityModalOpen = false;
  public isCountryModalOpen = false;
  public isNationalityModalOpen = false;
  public isPassportModalOpen = false;
  public maxDateLimit: string;
  public dobValidationMin: string;
  public dobValidationMax: string;

  constructor(
    private modalController: ModalController,
    private accountService: AccountService,
    private formBuilder: UntypedFormBuilder,
    private uiService: UiService,
    private analyticsService: AnalyticsService,
    private themeService: ThemeService
  ) {}

  ngOnInit() {
    this.currentHeaderData = {
      modalSlotEnd: true,
      marl20: true,
      title: this.guest && this.guest?.firstName ? 'Edit Guest' : 'Add Guest',
    };

    this.selectedCountryCode = this.themeService.getDefaultCountry;

    this.guestId = this.guest?.id || null;
    this.guestSource = this.guest?.source || null;

    const today = new Date();
    this.maxDateLimit = this.formatDateForInput(today);
    
    const hundredYearsAgo = new Date(today);
    hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
    this.dobValidationMin = this.formatDateForInput(hundredYearsAgo);
    this.dobValidationMax = this.maxDateLimit;

    this.guestForm = this.formBuilder.group(
      {
        abbreviation: [this.guest?.abbreviation ?? null, Validators.required],
        firstName: [this.guest?.firstName ?? null, Validators.required],
        lastName: [this.guest?.lastName ?? null, Validators.required],
        email: [
          this.guest?.email ?? null,
          [Validators.required, Validators.email],
        ],
        phoneNo: [
          this.guest?.phoneNo ?? null,
          [
            Validators.required,
            Validators.pattern('^[0-9]+$'),
            Validators.minLength(7),
            Validators.maxLength(15),
          ],
        ],
        gender: [this.guest?.gender ?? null, Validators.required],
        dob: [this.guest?.dob ?? null, Validators.required],
        type: [this.guest?.type ?? null, Validators.required],
        callingCode: [this.guest?.callingCode ?? null],
        passportNo: [
          this.guest?.passportNo ?? null,
          [Validators.pattern('^[A-Z0-9]{6,}$')],
        ],
        passportExpiryDate: [this.guest?.passportExpiryDate ?? null],
        passportIssueDate: [this.guest?.passportIssueDate ?? null],
        passportIssuedCountry: [this.guest?.passportIssuedCountry ?? null],
        passportIssuedCountryCode: [
          this.guest?.passportIssuedCountryCode ?? null,
        ],
        city: [this.guest?.city ?? null],
        country: [this.guest?.country ?? null],
        nationality: [this.guest?.nationality ?? this.themeService.getDefaultNationality?.nationality ?? null],
        address: [this.guest?.address ?? null],
      },
      {
        validators: [this.ageTypeValidator(), this.passportDatesValidator()],
      }
    );

    this.updateDobDateLimits(this.guestForm.get('type')?.value);
    this.guestForm.get('type')?.valueChanges.subscribe((type) => {
      this.updateDobDateLimits(type);
      this.guestForm.get('dob')?.updateValueAndValidity();
    });
  }

  private formatDateForInput(date: Date): string {
    return date.toLocaleDateString('en-CA');
  }

  private getDateYearsAgo(years: number): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - years);
    return this.formatDateForInput(date);
  }

  private updateDobDateLimits(type: string): void {
    const hundredYearsAgo = this.getDateYearsAgo(100);
    this.dobValidationMin = hundredYearsAgo;
    this.dobValidationMax = this.maxDateLimit;

    if (type === 'child') {
      this.dobValidationMin = this.getDateYearsAgo(12);
      this.dobValidationMax = this.getDateYearsAgo(2);
      return;
    }

    if (type === 'adult') {
      this.dobValidationMax = this.getDateYearsAgo(12);
      return;
    }

    if (type === 'infant') {
      this.dobValidationMin = this.getDateYearsAgo(2);
    }
  }

  ageTypeValidator(): ValidatorFn {
    return (group: AbstractControl): { [key: string]: any } | null => {
      const dobValue = group.get('dob')?.value;
      const type = group.get('type')?.value;

      if (!dobValue || !type) return null;

      const dob = new Date(dobValue);
      const today = new Date();

      // Set both dates to start of the day (ignores time part)
      dob.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Calculate total difference in days
      const diffInDays = Math.floor(
        (today.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffInDays < 0) return { futureDob: true };

      // Convert days to exact age in years with months/days accuracy
      const ageDate = new Date(today.getTime() - dob.getTime());
      const age = ageDate.getUTCFullYear() - 1970;

      if (type === 'infant' && diffInDays >= 730) {
        return { invalidInfantAge: true }; // 2 * 365
      }

      if (type === 'child' && (diffInDays < 730 || diffInDays >= 4383)) {
        return { invalidChildAge: true }; // 12 * 365.25 = 4383
      }

      if (type === 'adult' && diffInDays < 4383) {
        return { invalidAdultAge: true };
      }

      return null;
    };
  }

  passportDatesValidator(): ValidatorFn {
    return (group: AbstractControl): { [key: string]: any } | null => {
      const issueDate = new Date(group.get('passportIssueDate')?.value);
      const expiryDate = new Date(group.get('passportExpiryDate')?.value);
      const today = new Date();

      if (group.get('passportIssueDate')?.value && issueDate > today) {
        return { futureIssueDate: true };
      }

      if (
        group.get('passportIssueDate')?.value &&
        group.get('passportExpiryDate')?.value
      ) {
        if (expiryDate <= issueDate) return { invalidPassportDates: true };
        if (expiryDate <= today) return { expiredPassport: true };
      }

      return null;
    };
  }

  async selectCountryCode(fieldName: string) {
    const modal = await this.modalController.create({
      component: CountryCodeModalComponent,
    });
    modal.present();

    const { data } = await modal.onWillDismiss();
    this.selectedCountryCode = data || this.selectedCountryCode;
  }

  async saveGuest() {
    const loaderInstance = await this.uiService.presentGlobalLoading(
      'Saving...'
    );
    if (this.guestId) {
      this.guest.source = this.guestSource;
      this.accountService
        .editUserGuest(this.guestId, this.guest)
        .subscribe(() => {
          loaderInstance.dismiss();
          this.modalController.dismiss();
        },(error) => {
          this.analyticsService.trackError(error);
        });
    } else {
      this.accountService.addUserGuest(this.guest).subscribe((response) => {
        loaderInstance.dismiss();
        this.modalController.dismiss();
      },(error) => {
        this.analyticsService.trackError(error);
      });
    }
  }

  closeModal() {
    this.modalController.dismiss();
  }

  handleGuestDetails() {
    this.analyticsService.trackUnified('guest_add_submitted', {
      flow: 'account',
      outcome: 'success',
      legacy_event_name: 'Add Guest',
      component: 'guest-form-component',
      button: 'add-guest-button',
    });
    if (this.guestForm.valid) {
      console.log('Guest Details:', this.guestForm.value);

      this.guest = {
        ...this.guestForm.value,
        callingCode: this.selectedCountryCode.dialCode,
      };
      this.saveGuest();
      // Handle form submission logic here
    }
  }

  async selectPassportIssuePlace() {
    if (this.isPassportModalOpen) return;
    this.isPassportModalOpen = true;
    const modal = await this.modalController.create({
      component: CountryCodeModalComponent,
    });
    modal.present();

    const { data } = await modal.onWillDismiss();
    if (data?.name) {
      this.guestForm.patchValue({
        passportIssuedCountry: data.name,
        passportIssuedCountryCode: data.code,
      });
    }
    this.isPassportModalOpen = false;
  }

  async selectCountry() {
    if (this.isCountryModalOpen) return;
    this.isCountryModalOpen = true;
    const modal = await this.modalController.create({
      component: CountryCodeModalComponent,
    });
    modal.present();

    const { data } = await modal.onWillDismiss();
    if (data?.name) {
      this.guestForm.patchValue({ country: data.name });
    }
    this.isCountryModalOpen = false;
  }

  async selectNationality() {
    if (this.isNationalityModalOpen) return;
    this.isNationalityModalOpen = true;
    const modal = await this.modalController.create({
      component: SelectCountryModalComponent,
      backdropDismiss: false,
    });

    modal.onDidDismiss().then((data) => {
      this.isNationalityModalOpen = false;
      if (data?.data?.nationality) {
        this.guestForm.patchValue({ nationality: data.data.nationality });
      }
    });

    return await modal.present();
  }

  async selectCity() {
    if (this.isCityModalOpen) return;
    this.isCityModalOpen = true;
    const modal = await this.modalController.create({
      component: CityListComponent,
      backdropDismiss: false,
    });

    modal.onDidDismiss().then((data) => {
      this.isCityModalOpen = false;
      if (data.data?.city?.city) {
        this.guestForm.patchValue({ city: data.data.city.city });
      }
    });

    return await modal.present();
  }
}
